import React, {useEffect, useState} from 'react';
import {AppTextEditor, EventReminderButton, EventReminderPanel, FormPageRoot, ThemedButton, ThemedInput} from "../../../index";
import {useTheme} from "../../../../common/ProjectThemeProvider";
import {Tooltip} from "@mui/material";
import {
    checkNullArr,
    checkNullJson,
    checkNullStr,
    createTimeout,
    formatDateToIoString,
    getArrIndexValue,
    getCurrUrlParamValue,
    getJsonValue,
    getJsonValueFromNestedKeys,
    getStrSplitArrIndexValue,
    isBoolTrue,
    isDateExpired,
    isJsonValueTrue,
    parseInteger,
    trim
} from "../../../../common/fusion-ui/utils/ReactUtils";
import {focusFormIdElement, isCurrUserRoleAdmin, printError, printLog, sendRequest} from "../../../utils/AppUtils";
import {useToast} from "../../../../common/fusion-ui/provider/ToastProvider";
import {
    EVENT_INPUT_MAX_LEN,
    EventFormMemberType as PRESENTER_MEMBER_TYPES,
    EventFormParticipantTypes as PARTICIPANT_TYPES,
    EventFormRepeatTypes as REPEAT_TYPES,
    GenRouteUrls,
    INPUT_FILE_MAX_SIZE,
    ModuleFieldsData,
    ModuleRouteUrls,
    Modules,
    ReqUrls,
    ShowViewType,
} from "../../../config/AppConfig";
import {
    Avatar,
    Checkbox,
    DurationPicker,
    Flexbox,
    GridBox,
    Label,
    MsgBox,
    PdfViewer,
    RadioToggle,
    SearchableCheckboxList,
    SearchableSelect
} from "../../../../common/fusion-ui";
import {Constants, CssVariant, Messages} from "../../../../common/fusion-ui/config/FusionConfig";
import {useInternetStatus} from "../../../../common/fusion-ui/hook";
import {UnderMaintenance, UnderMaintenanceMsg} from "../../../../common/CommonConfig";

const Meeting_AddEdit_Page = React.memo(({
                                             updateOnly,
                                             open,
                                             onHide,
                                             recordId,
                                             showAs = ShowViewType.PAGE,
                                             onSuccess,
                                         }) => {
    const TAG = "Meeting_AddEdit_Page";

    const moduleName = Modules.Meetings;
    const mf = ModuleFieldsData[moduleName];
    const NAME_KEY = 'name';

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isOnline, internetCheckingMsg} = useInternetStatus();

    const DUR_SEP = ':';
    const FOCUS_DELAY = 2000;
    const REMINDER_TYPES = {
        //Do not send reminders
        // On event creation
        // 15 days before
        // 7 days before
        // 3 days before
        // One day before
        // 3 hours before
        // 1 hour before
        // 15 minutes before
        doNot: {
            title: "Do not send reminders",
            column_Email: ModuleFieldsData[moduleName].reminder_doNotEmail.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_doNotSms.column,
        },
        event: {
            title: "On meeting creation",
            column_Email: ModuleFieldsData[moduleName].reminder_eventEmail.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_eventSms.column,
        },
        _15DaysBefore: {
            title: "15 days before",
            column_Email: ModuleFieldsData[moduleName].reminder_day15Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_day15Sms.column,
        },
        _7DaysBefore: {
            title: "7 days before",
            column_Email: ModuleFieldsData[moduleName].reminder_day7Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_day7Sms.column,
        },
        _3DaysBefore: {
            title: "3 days before",
            column_Email: ModuleFieldsData[moduleName].reminder_day3Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_day3Sms.column,
        },
        _1DaysBefore: {
            title: "One day before",
            column_Email: ModuleFieldsData[moduleName].reminder_day1Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_day1Sms.column,
        },
        _3HrBefore: {
            title: "3 hours before",
            column_Email: ModuleFieldsData[moduleName].reminder_hrs3Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_hrs3Sms.column,
        },
        _1HrBefore: {
            title: "1 hour before",
            column_Email: ModuleFieldsData[moduleName].reminder_hrs1Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_hrs1Sms.column,
        },
        _15MinBefore: {
            title: "15 minutes before",
            column_Email: ModuleFieldsData[moduleName].reminder_min15Email.column,
            column_Sms: ModuleFieldsData[moduleName].reminder_min15Sms.column,
        },
    };
    const MEETING_TYPES = {
        external: "External Meeting",
        internal: "Conference",
    };


    const Fields = {
        updateRecordId: 'updateRecordId',

        canShowAddForm: 'canShowAddForm',
        isUpdateFormDataFetched: 'isUpdateFormDataFetched',

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',

        participantsLoading: 'participantsLoading',
        resetParticipantsList: 'resetParticipantsList',
        participantsLoaded: 'participantsLoaded',
        participantsLoadingMsg: 'participantsLoadingMsg',

        submitBtn_AsLoading: 'submitBtn_AsLoading',
        submitBtn_disabled: 'submitBtn_disabled',

        presentValueObj: 'presentValueObj',
        currParticipantGroupId: 'currParticipantGroupId',
        isPdfFileLoading: 'isPdfFileLoading',

        openEventReminderPopup: 'openEventReminderPopup',
        recordId_eventReminder: 'recordId_eventReminder',

        panelMsg: 'panelMsg',
        panelMsgColor: 'panelMsgColor',
    };
    const [fieldsData, setFieldsData] = useState({});
    // const [formData, setFormData] = useState(Meeting_AddEdit_Page_FormData);
    const [formData, setFormData] = useState({});
    const [formFieldsMsgs, setFormFieldsMsgs] = useState({});

    useEffect(() => {
        if (!checkNullStr(getFormField(mf.memberType)) && !isUpdateMode()) {
            // updateFormField(mf.participantNames, sliceArr(UserList, 0, 20));
            updateFormField(mf.memberType, PRESENTER_MEMBER_TYPES.member);
            updateFormField(mf.repeat, REPEAT_TYPES.doNotRepeat);
            updateFormField(mf.participantDisplayType, PARTICIPANT_TYPES.membersOnly);
            updateFormField(mf.type, MEETING_TYPES.external);
            updateFormField(mf.timezone, true);
        }

        // log('useEffect.showAs:', showAs, 'recordId:', recordId);
        if (showAs == ShowViewType.PAGE) {
            if (isUpdateMode()) {
                const id = getCurrUrlParamValue('id');
                fetchEditModeData(id);
            }
        } else {
            if (checkNullStr(recordId) && isUpdateMode()) {
                fetchEditModeData(recordId, false);
            }
        }
        if (!isUpdateMode()) {
            fetchParticipantList([], true);
        }
    }, [recordId, showAs, updateOnly]);

    return (<>
        <FormPageRoot
            tag={TAG}
            moduleName={moduleName}
            addPageTitle={getTabTitle()}
            editPageTitle={getTabTitle()}
            updateOnly={isUpdateMode()}
            isDataLoading={getField(Fields.isDataLoading)}
            isDataFound={getField(Fields.isDataFound)}
            dataLoadingMsg={getField(Fields.dataLoadingMsg)}
            open={open}
            onHide={() => {
                if (onHide) onHide();
                clearPanelData();
            }}
            showAs={showAs}
        >
            <div>
                {/*top-section*/}
                <div>
                    {/*date*/}
                    <ThemedInput
                        mainWrapperClassName={''}
                        wrapperClassName={''}
                        className={''}
                        label={mf.date.titleAlt2}
                        id={mf.date.column}
                        type={'datetime-local'}
                        // defaultValue={getFormField(mf.date)}
                        value={getFormField(mf.date)}
                        msg={getFormFieldMsg(mf.date)}
                        required={true}
                        onChange={(e) => {
                            const value = e.target.value;//2024-04-19T15:34
                            updateFormField(mf.date, value);

                            //get am/pm as well
                            try {
                                // Extract the time portion from the selected date-time string
                                const time = (value + '').split('T')[1].substring(0, 5);
                                const hour = parseInt(time.split(':')[0]);

                                // Determine whether it's AM or PM based on the hour
                                const period = hour >= 12 ? 'PM' : 'AM';
                                // log('date-ampm.input.onChange.period:', period, 'value:', value);
                                updateFormField(mf.ampm, period);
                            } catch (error) {
                                logErr('date-ampm.input.onChange.period.error:', error);
                            }
                            if (checkNullStr(value)) {
                                updateFormFieldMsg(mf.date, '');
                            }
                        }}
                    />

                    {/*DurationPicker*/}
                    <div className={'m-0 p-0 mt-3x rounded-3'}>
                        {createLabelField(mf.duration.title, true)}
                        <DurationPicker
                            inputId={mf.duration.column}
                            showHours={true}
                            showMinutes={true}
                            showSeconds={false}
                            defHour={parseInteger(getStrSplitArrIndexValue(getFormField(mf.duration), 0, DUR_SEP))}
                            defMinute={parseInteger(getStrSplitArrIndexValue(getFormField(mf.duration), 1, DUR_SEP))}
                            onChange={(hour, min, sec) => {
                                //log('duration.DurationPicker.onChange.hour', hour, 'min:', min, 'sec:', sec);
                                updateFormField(mf.duration, `${hour}${DUR_SEP}${min}`)
                                // updateFormField(mf.duration, `${parseInteger(hour) < 10 ? `0${hour}` : hour}${DUR_SEP}${parseInteger(min) < 10 ? `0${min}` : min}`)
                            }}
                        />
                    </div>

                    {/*timezone*/}
                    <div className={'mt-3'}>
                        {/*timezone*/}
                        <Checkbox
                            wrapperClassName={''}
                            className={''}
                            defaultChecked={isFormFieldTrue(mf.timezone)}
                            checked={isFormFieldTrue(mf.timezone)}
                            onChange={(e, value) => {
                                // log('timezone.cb.onChange.value:', value);
                                updateFormField(mf.timezone, value);
                            }}>
                            {mf.timezone.title}
                        </Checkbox>
                    </div>

                    {/*type*/}
                    <div className={'mt-1'}>
                        <RadioToggle
                            className={`bg-infox px-3x`}
                            radioClassName={'w-auto'}
                            labelClassName={'fw-semi-bold text-input-label'}
                            labelColor={theme.text40}
                            cbPy={0}
                            checked={getFormField(mf.type) == MEETING_TYPES.external}
                            config={{
                                label: mf.type.title,
                                active: MEETING_TYPES.external,
                                inactive: MEETING_TYPES.internal,
                            }}
                            onChange={(e, value) => {
                                log('type.RadioToggle.onChange.value:', value);
                                updateFormField(mf.type, value);
                                if (!isUpdateMode()) {
                                    updateFormField(mf.type, '');
                                }
                            }}
                        />
                    </div>

                    {/*title & outline*/}
                    <div className={'mt-3'}>
                        {/*title*/}
                        <ThemedInput
                            wrapperClassName={''}
                            className={''}
                            label={mf.title.title}
                            placeholder={mf.title.title}
                            id={mf.title.column}
                            type={'text'}
                            value={getFormField(mf.title)}
                            showLimitLabel={true}
                            maxLength={EVENT_INPUT_MAX_LEN}
                            required={true}
                            onChange={(e) => {
                                updateFormField(mf.title, e.target.value);
                            }}
                        />

                        {/*outline*/}
                        <ThemedInput
                            mainWrapperClassName={'w-100'}
                            wrapperClassName={'w-100'}
                            className={'w-100'}
                            label={mf.outline.title}
                            placeholder={mf.outline.title}
                            value={getFormField(mf.outline)}
                            id={mf.outline.column}
                            showLimitLabel={true}
                            maxLength={EVENT_INPUT_MAX_LEN}
                            required={true}
                            asTextArea={true}
                            style={{
                                height: '80px',
                                minHeight: '80px',
                                maxHeight: '80px'
                            }}
                            onChange={(e) => {
                                updateFormField(mf.outline, e.target.value);
                            }}
                        />
                    </div>
                </div>

                {/*desc-editor*/}
                <div className={'mt-x'}>
                    {createLabelField(mf.desc.titleAlt, true)}
                    <div className={'sectionx rounded-3x w-100'}>
                        <AppTextEditor
                            id={mf.desc.column}
                            value={getFormField(mf.desc)}
                            onChange={(content) => {
                                // log('AppTextEditor.onChange.content:', content);
                                updateFormField(mf.desc, content);
                            }}/>
                    </div>
                </div>

                {/*venue & img*/}
                <div className={'mt-3 position-relative'}>
                    {/*venue*/}
                    <ThemedInput
                        mainWrapperClassName={'pb-3'}
                        wrapperClassName={''}
                        className={''}
                        label={mf.venue.titleAlt2}
                        placeholder={mf.venue.title}
                        type={'text'}
                        value={getFormField(mf.venue)}
                        id={mf.venue.column}
                        showLimitLabel={true}
                        maxLength={EVENT_INPUT_MAX_LEN}
                        required={true}
                        showMsg={false}
                        onChange={(e) => {
                            updateFormField(mf.venue, e.target.value);
                        }}
                    />
                </div>

                {/*  memberType-toggle  */}
                <div className={'mt-3x'}>
                    <RadioToggle
                        className={`bg-infox px-3x`}
                        radioClassName={'w-auto'}
                        labelClassName={'fw-semi-bold text-input-label'}
                        labelColor={theme.text40}
                        cbPy={0}
                        required={true}
                        checked={getFormField(mf.memberType) == PRESENTER_MEMBER_TYPES.member}
                        config={{
                            label: mf.presenter.title,
                            active: PRESENTER_MEMBER_TYPES.member,
                            inactive: PRESENTER_MEMBER_TYPES.notMember,
                        }}
                        onChange={(e, value) => {
                            // log('memberType.RadioToggle.onChange.value:', value);
                            updateFormField(mf.memberType, value);
                            if (!isUpdateMode()) {
                                updateFormField(mf.presenter, '');
                                updateField(Fields.presentValueObj, {});
                            }
                        }}
                    />
                    <div>
                        {/*form for PRESENTER_MEMBER_TYPES.member*/}
                        {(getFormField(mf.memberType) == PRESENTER_MEMBER_TYPES.member)
                            && <div className={'mt-3'}>
                                <SearchableSelect
                                    label={`Choose a presenter`}
                                    wrapperClassName={`fd-column`}
                                    className={`w-100 m-0 mt-0`}
                                    labelClassName={'pb-1'}
                                    labelAsPlaceholder={true}
                                    multiple={false}
                                    searchable={true}
                                    required={true}
                                    justifyAt={'start'}
                                    labelColor={theme.text40}
                                    options={getParticipantList(true).map(item => ({value: item.name, label: item.name}))}
                                    defOption={getField(Fields.presentValueObj, {})}
                                    onChange={(data, label, value) => {
                                        // const label = data.label;
                                        // const value = data.value;
                                        // log('SearchableSelect.onChange.data:', data, 'label:', label, 'Value:', value);
                                        updateFormField(mf.presenter, label);
                                        updateField(Fields.presentValueObj, data);
                                    }}/>
                            </div>}

                        {/*form for PRESENTER_MEMBER_TYPES.notMember*/}
                        {(getFormField(mf.memberType) == PRESENTER_MEMBER_TYPES.notMember)
                            && <div className={'mt-2'}>
                                {createLabelField('Please add new member details', false, theme.text80, 'mt-1')}
                                {/*presenter*/}
                                <ThemedInput
                                    wrapperClassName={''}
                                    className={''}
                                    label={mf.presenter.title}
                                    placeholder={mf.presenter.title}
                                    id={mf.presenter.column}
                                    showLimitLabel={true}
                                    maxLength={EVENT_INPUT_MAX_LEN}
                                    type={'text'}
                                    value={getFormField(mf.presenter)}
                                    required={true}
                                    onChange={(e) => {
                                        updateFormField(mf.presenter, e.target.value);
                                    }}
                                />

                                {/*newPresenterQualification*/}
                                <ThemedInput
                                    wrapperClassName={''}
                                    className={''}
                                    label={mf.newPresenterQualification.title}
                                    placeholder={mf.newPresenterQualification.title}
                                    id={mf.newPresenterQualification.column}
                                    showLimitLabel={true}
                                    maxLength={EVENT_INPUT_MAX_LEN}
                                    type={'text'}
                                    value={getFormField(mf.newPresenterQualification)}
                                    onChange={(e) => {
                                        updateFormField(mf.newPresenterQualification, e.target.value);
                                    }}
                                />

                                {/*newPresenterDesignation*/}
                                <ThemedInput
                                    wrapperClassName={''}
                                    className={''}
                                    label={mf.newPresenterDesignation.title}
                                    placeholder={mf.newPresenterDesignation.title}
                                    id={mf.newPresenterDesignation.column}
                                    showLimitLabel={true}
                                    maxLength={EVENT_INPUT_MAX_LEN}
                                    type={'text'}
                                    value={getFormField(mf.newPresenterDesignation)}
                                    onChange={(e) => {
                                        updateFormField(mf.newPresenterDesignation, e.target.value);
                                    }}
                                />

                                {/*newPresenterOrg*/}
                                <ThemedInput
                                    wrapperClassName={''}
                                    className={''}
                                    label={mf.newPresenterOrg.title}
                                    placeholder={mf.newPresenterOrg.title}
                                    id={mf.newPresenterOrg.column}
                                    showLimitLabel={true}
                                    maxLength={EVENT_INPUT_MAX_LEN}
                                    type={'text'}
                                    value={getFormField(mf.newPresenterOrg)}
                                    onChange={(e) => {
                                        updateFormField(mf.newPresenterOrg, e.target.value);
                                    }}
                                />

                                {/*newPresenterBio*/}
                                <ThemedInput
                                    wrapperClassName={''}
                                    className={''}
                                    label={mf.newPresenterBio.title}
                                    placeholder={mf.newPresenterBio.title}
                                    id={mf.newPresenterBio.column}
                                    showLimitLabel={true}
                                    maxLength={EVENT_INPUT_MAX_LEN}
                                    type={'text'}
                                    value={getFormField(mf.newPresenterBio)}
                                    asTextArea={true}
                                    style={{
                                        height: '80px',
                                        minHeight: '80px',
                                        maxHeight: '80px'
                                    }}
                                    onChange={(e) => {
                                        updateFormField(mf.newPresenterBio, e.target.value);
                                    }}
                                />

                                {/*newPresenterImage*/}
                                <Avatar
                                    wrapperClassName={'w-100'}
                                    className={'mt-1'}
                                    labelClassName={'w-100x pb-1 text-startxx'}
                                    justifyAt={'center'}
                                    alignAt={'start'}
                                    wrap={'wrap'}
                                    direction={'column'}
                                    label={mf.newPresenterImage.title}
                                    labelColor={theme.text40}
                                    accept={'.jpg, .jpeg, .png'}
                                    editTooltip={`Edit (.jpg, .jpeg, .png)`}
                                    showEdit={true}
                                    showDelete={true}
                                    circular={false}
                                    showFullPreviewDialog={true}
                                    editSize={'21px'}
                                    size={'100px'}
                                    maxSize={INPUT_FILE_MAX_SIZE}
                                    onChange={(e, file, path, name) => {
                                        //log('Avatar.newPresenterImage.onChange.name:', name);
                                        updateFormField(mf.newPresenterImage, file);
                                    }}
                                    onInvalidFormat={(e, name) => {
                                        toast(`Invalid file format. File must be jpg or jpeg or png`, CssVariant.danger);
                                    }}
                                    onSizeExceeded={() => {
                                        toast(`Max size exceeded: ${INPUT_FILE_MAX_SIZE}`, CssVariant.danger);
                                    }}>{getFormField(mf.newPresenterImage)}</Avatar>
                            </div>}
                    </div>
                </div>

                {/* participants cb-list   */}
                <div className={'mt-3'}>
                    {createLabelField(mf.participants.title, true)}

                    {/*participantDisplayType*/}
                    <RadioToggle
                        className={`bg-infox px-3x`}
                        radioClassName={'w-auto'}
                        labelClassName={'fw-semi-bold text-input-label'}
                        labelColor={theme.text40}
                        cbPy={0}
                        required={true}
                        checked={getFormField(mf.participantDisplayType) == PARTICIPANT_TYPES.membersOnly}
                        config={{
                            // label: "Participant Type",
                            active: PARTICIPANT_TYPES.membersOnly,
                            inactive: PARTICIPANT_TYPES.groupsOnly,
                        }}
                        onChange={(e, value) => {
                            // log('participantDisplayType.RadioToggle.onChange.value:', value);
                            updateFormField(mf.participantDisplayType, value);
                            // updateFormField(mf.presenter, '');
                        }}
                    />
                    {/*participants & groups*/}
                    <div className={'px-2'}>
                        {/*groups grid*/}
                        {(getFormField(mf.participantDisplayType) == PARTICIPANT_TYPES.groupsOnly) && <div>
                            {/*<div>currParticipantGroupId: {getField(Fields.currParticipantGroupId)}</div>*/}
                            {(checkNullArr(getParticipantGroupsList()))
                                ? <GridBox className={'px-3'} sm={12} md={4} gap={0}>
                                    {getParticipantGroupsList().map((cbItem, index) => {
                                        return (checkNullJson(cbItem)) && <Checkbox
                                            key={cbItem[NAME_KEY] + index}
                                            wrapperClassName={''}
                                            className={''}
                                            labelClassName={'fw-semi-bold'}
                                            // defaultChecked={getParticipantGroupsList().some(item => item[NAME_KEY] == cbItem[NAME_KEY] && isBoolTrue(cbItem.selected))}
                                            // checked={getParticipantGroupsList().some(item => item[NAME_KEY] == cbItem[NAME_KEY] && isBoolTrue(cbItem.selected))}
                                            defaultChecked={getField(Fields.currParticipantGroupId) == cbItem.id}
                                            checked={getField(Fields.currParticipantGroupId) == cbItem.id}
                                            onChange={(e, value) => {
                                                updateField(Fields.currParticipantGroupId, value ? cbItem.id : null);
                                                log('participantGroupNames.map.cb.onChange.value:', value, 'id:', cbItem.id);

                                                let names = getParticipantGroupsList();
                                                names = names.map((nameItem) => {
                                                    return {
                                                        ...nameItem,
                                                        selected: nameItem[NAME_KEY] == cbItem[NAME_KEY] ? value : isBoolTrue(nameItem.selected),
                                                    }
                                                });
                                                updateFormField(mf.participantGroupNames, names);
                                                // log('participantGroupNames.map.cb.onChange.value:', value, '\nnames:', names);
                                                // log('curr-cbItem.name:', cbItem.name, 'curr-name-found', names.some(item => (isJsonValueTrue(item,'selected'))));
                                            }}
                                        >{cbItem.name}</Checkbox>
                                    })}
                                </GridBox>
                                : createLabelField('No groups found!', false, theme.text, '')}
                        </div>}

                        {/*participant-list*/}
                        <div className={'mt-2'}>
                            <SearchableCheckboxList
                                roundedVariant={4}
                                listHeight={'50vh'}
                                searchPlaceholder={'Search members...'}
                                borderColor={theme.text30}
                                showSelect={false}
                                pad={2}
                                checkListColumnCount={3}
                                showDisplaySelectedOnly={true}
                                isCheckItemJson={true}
                                checkItemJsonIdKey={'id'}
                                checkItemJsonTextKey={'name'}
                                checkDataList={getParticipantList()}
                                onValueChange={(e, value, selectedList) => {
                                    // log('participantNames-searchList.onValueChange.selectedList:', selectedList, 'len:', selectedList.length);
                                    updateFormField(mf.participantNamesSelectedOnly, selectedList);
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/*related-docs*/}
                <div className={'mt-3'}>
                    {/*document-img*/}
                    <GridBox className={'px-2'} md={[4, 8]} sm={[12, 12]}>
                        <div className={' px-2'}>
                            <Avatar
                                wrapperClassName={'w-100'}
                                className={''}
                                labelClassName={'w-100x pb-2 text-startxx'}
                                justifyAt={'center'}
                                alignAt={'start'}
                                wrap={'wrap'}
                                direction={'column'}
                                label={mf.doc.title}
                                labelColor={theme.text40}
                                accept={'.pdf, .jpg, .jpeg, .png'}
                                editTooltip={`Edit (.pdf, .jpg, .jpeg, .png)`}
                                showEdit={true}
                                showDelete={true}
                                circular={false}
                                dottedExt={true}
                                showFullPreviewDialog={true}
                                defAvatarImg={Constants.docVector}
                                editSize={'21px'}
                                width={'100px'}
                                height={'140px'}
                                maxSize={INPUT_FILE_MAX_SIZE}
                                onChange={(e, file, path, name, ext) => {
                                    log('Avatar.doc.onChange.name:', name, 'ext:', ext);
                                    updateFormField(mf.doc, file);
                                    updateFormField(mf.docFileExt, ext);
                                }}
                                onInvalidFormat={(e, name) => {
                                    toast(`Invalid file format. File must be pdf, jpg, jpeg or png`, CssVariant.danger);
                                }}
                                onSizeExceeded={() => {
                                    toast(`Max size exceeded: ${INPUT_FILE_MAX_SIZE}`, CssVariant.danger);
                                }}
                                onFileLoading={(loading) => {
                                    updateField(Fields.isPdfFileLoading, isBoolTrue(loading));
                                }}>{getFormField(mf.doc)}</Avatar>
                        </div>

                        {/*pdf-viewer*/}
                        {(checkNullStr(getFormField(mf.doc)) &&
                                (getFormField(mf.docFileExt) + '').toLowerCase().trim().endsWith('.pdf')
                                && !isFieldTrue(Fields.isPdfFileLoading)) &&
                            <div className={'ps-3x'}>
                                <PdfViewer
                                    label={`${mf.doc.titleAlt} Preview`}
                                    pdfTitle={mf.doc.titleAlt}
                                    src={getFormField(mf.doc)}
                                    labelColor={theme.text}
                                    height={'240px'}
                                />
                            </div>}
                    </GridBox>
                    {/*docDesc*/}
                    <ThemedInput
                        wrapperClassName={'w-100 mt-3 pb-3'}
                        className={''}
                        label={mf.docDesc.title}
                        placeholder={mf.docDesc.title}
                        id={mf.docDesc.column}
                        showLimitLabel={true}
                        maxLength={EVENT_INPUT_MAX_LEN}
                        type={'text'}
                        value={getFormField(mf.docDesc)}
                        showMsg={false}
                        onChange={(e) => {
                            updateFormField(mf.docDesc, e.target.value);
                        }}
                    />
                </div>

                {/*repeat*/}
                <div className={'mt-3x'}>
                    {createLabelField(mf.repeat.title)}
                    {/*repeat-radios*/}
                    <GridBox className={'px-3'} sm={6} md={3} gap={0} gapX={1}>
                        {Object.values(REPEAT_TYPES).map((item, index) => {
                            return <Checkbox
                                key={item + index}
                                wrapperClassName={''}
                                className={''}
                                type={'radio'}
                                py={0}
                                // defaultChecked={isFormFieldTrue(mf.repeat)==item}
                                checked={getFormField(mf.repeat) == item}
                                onChange={(e, value) => {
                                    // log('REPEAT_TYPES.map.cb.onChange.value:', value);
                                    updateFormField(mf.repeat, item);
                                    updateFormField(mf.repeatDate, '');
                                }}>{item}</Checkbox>
                        })}
                    </GridBox>

                    {/*repeatDate input for date-repeat type*/}
                    {(getFormField(mf.repeat) == REPEAT_TYPES.date) &&
                        <div className={'mt-1'}>
                            <ThemedInput
                                mainWrapperClassName={''}
                                wrapperClassName={''}
                                labelClassName={'px-2'}
                                label={mf.repeatDate.title}
                                id={mf.repeatDate.column}
                                type={'datetime-local'}
                                // required={getFormField(mf.repeat) == REPEAT_TYPES.date}
                                value={getFormField(mf.repeatDate)}
                                onChange={(e) => {
                                    const value = e.target.value;//2024-04-19T15:34
                                    updateFormField(mf.repeatDate, value);

                                    //get am/pm as well
                                    try {
                                        // Extract the time portion from the selected date-time string
                                        const time = (value + '').split('T')[1].substring(0, 5);
                                        const hour = parseInt(time.split(':')[0]);

                                        // Determine whether it's AM or PM based on the hour
                                        const period = hour >= 12 ? 'PM' : 'AM';
                                        // log('repeatAmPm.input.onChange.period:', period, 'value:', value);
                                        updateFormField(mf.repeatAmPm, period);
                                    } catch (error) {
                                        logErr('repeatAmPm.input.onChange.period.error:', error);
                                    }
                                }}
                            />
                        </div>}
                </div>

                {/*reminders*/}
                <div className={'mt-x3'}>
                    {createLabelField(mf.reminders.title)}
                    {/*repeat-radios & cbs in table*/}
                    <div className={'px-2 mt-n1'}>
                        <table className={'w-100 m-0 p-0 '} style={{color: theme.text}}>
                            <thead>
                            <tr>
                                <th>{mf.reminders.titleAlt}</th>
                                <th className={'text-nowrap text-center-forcex'}>
                                    <input className={''}
                                           type="checkbox"
                                        // defaultChecked={isFormFieldTrue(item.column_Email, true)}
                                        // checked={isFormFieldTrue(item.column_Email, true)}
                                           onChange={(e) => {
                                               const checked = e.target.checked;
                                               const remColumns = Object.values(REMINDER_TYPES)
                                                   .filter(item => item.column_Email != REMINDER_TYPES.doNot.column_Email)
                                                   .map(item => item.column_Email)
                                                   .map(item => ({[item]: checked}))
                                                   .reduce((acc, obj) => ({...acc, ...obj}), {});
                                               log('Email-header: remColumns:', remColumns, 'checked:', checked);
                                               setFormData(prevState => ({...prevState, ...remColumns}));
                                           }}
                                    />
                                    <span>&nbsp;Email</span>
                                </th>
                                <th className={'text-nowrap text-center-forcex'}>
                                    <input className={''}
                                           type="checkbox"
                                        // defaultChecked={isFormFieldTrue(item.column_Sms, true)}
                                        // checked={isFormFieldTrue(item.column_Sms, true)}
                                           onChange={(e) => {
                                               const checked = e.target.checked;
                                               const remColumns = Object.values(REMINDER_TYPES)
                                                   .filter(item => item.column_Sms != REMINDER_TYPES.doNot.column_Sms)
                                                   .map(item => item.column_Sms)
                                                   .map(item => ({[item]: checked}))
                                                   .reduce((acc, obj) => ({...acc, ...obj}), {});
                                               log('Sms-header: remColumns:', remColumns, 'checked:', checked);
                                               setFormData(prevState => ({...prevState, ...remColumns}));
                                           }}
                                    />
                                    <span>&nbsp;SMS</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {Object.values(REMINDER_TYPES).map((item, index) => {
                                return (checkNullJson(item)) && <tr
                                    className={''}
                                    key={index + item.column_Email}>
                                    <td className={''}>{item.title}</td>
                                    {/*email-cb*/}
                                    <td className={'text-center-forcex'}>
                                        <input
                                            type="checkbox"
                                            defaultChecked={isFormFieldTrue(item.column_Email, true)}
                                            checked={isFormFieldTrue(item.column_Email, true)}
                                            onChange={(e) => {
                                                const checked = isBoolTrue(e.target.checked);
                                                // log('reminders.map.email-cb.onChange.checked:', checked, 'item:', item, 'formData', formData);
                                                updateFormField(item.column_Email, checked, true);
                                            }}
                                        />
                                    </td>
                                    {/*sms-cb*/}
                                    <td className={'text-center-forcex'}>
                                        <input
                                            type="checkbox"
                                            defaultChecked={isFormFieldTrue(item.column_Sms, true)}
                                            checked={isFormFieldTrue(item.column_Sms, true)}
                                            onChange={(e) => {
                                                const checked = isBoolTrue(e.target.checked);
                                                // log('reminders.map.sms-cb.onChange.checked:', checked, 'item:', item, 'formData', formData);
                                                updateFormField(item.column_Sms, checked, true);
                                            }}
                                        />
                                    </td>
                                </tr>
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>


                {/*footer*/}
                <div className={'mt-3'}>
                    {/*msg-box*/}
                    {checkNullStr(getField(Fields.panelMsg)) && <MsgBox color={getField(Fields.panelMsgColor, theme.text)}>
                        {getField(Fields.panelMsg)}
                    </MsgBox>}

                    {/* reminder send-btn */}
                    {(isUpdateMode() && isCurrUserRoleAdmin()) &&
                        <Flexbox className={'my-3 w-100'} justifyAt={'center'}>
                            <Tooltip title={isDateExpired(getFormField(mf.date)) ? `Can't send reminder for an expired event!` : ''}>
                                <div>
                                    {isCurrUserRoleAdmin() && <EventReminderButton
                                        ml={2}
                                        disabled={isDateExpired(getFormField(mf.date))}
                                        onClick={() => {
                                            if (checkNullArr(getFormField(mf.participantNamesSelectedOnly))) {
                                                updateField(Fields.openEventReminderPopup, true);
                                                updateField(Fields.recordId_eventReminder, getField(Fields.updateRecordId));
                                            } else {
                                                toast("Please select some participants first", CssVariant.warning);
                                            }
                                        }}
                                    />}
                                </div>
                            </Tooltip>
                        </Flexbox>}


                    {/*controls*/}
                    <Flexbox className={'mt-2'} justifyAt={'center'} alignAt={'center'}>
                        {/*cancel*/}
                        {(showAs == ShowViewType.DRAWER || showAs == ShowViewType.DIALOG) && <ThemedButton
                            className={'rounded-2 w-auto px-4 py-1'}
                            text={'Cancel'}
                            variant={theme.text10}
                            hoverBgColor={theme.text30}
                            color={theme.text}
                            onClick={() => {
                                if (onHide) onHide();
                                clearPanelData();
                            }}
                            mx={2}
                        />}

                        <ThemedButton
                            className={'rounded-2 w-auto px-4 py-1'}
                            // text={'Save'}
                            text={isFieldTrue(Fields.submitBtn_AsLoading)
                                ? isUpdateMode() ? 'Updating...' : 'Saving...'
                                : isUpdateMode() ? 'Update' : 'Save'}
                            variant={theme.primaryBlue}
                            hoverBgColor={theme.primaryBlueDark}
                            loadingWidth={'auto'}
                            asLoading={isFieldTrue(Fields.submitBtn_AsLoading)}
                            disabled={isFieldTrue(Fields.submitBtn_disabled)}
                            onClick={handleSubmit}
                        />
                    </Flexbox>
                </div>
            </div>
        </FormPageRoot>
        {(isUpdateMode() && isCurrUserRoleAdmin()) && createEventReminderPopup()}
    </>)

    function createEventReminderPopup() {
        return <EventReminderPanel
            moduleName={moduleName}
            recordId={getField(Fields.recordId_eventReminder)}
            recordUrl={`${GenRouteUrls.meeting_internalPage}${getField(Fields.recordId_eventReminder)}`}
            open={isFieldTrue(Fields.openEventReminderPopup)}
            // showAs={ShowViewType.DRAWER}
            recordData={{
                title: getFormField(mf.title),
                description: getFormField(mf.desc),
                date: getFormField(mf.date),
                venue: getFormField(mf.venue),
                selectedParticipants: checkNullArr(getFormField(mf.participantNamesSelectedOnly))
                    ? getFormField(mf.participantNamesSelectedOnly).map(item => ({id: getJsonValue(item, 'id')}))
                    : [],
            }}
            onDoneAndHide={() => {
                updateField(Fields.openEventReminderPopup, false);
                updateField(Fields.recordId_eventReminder, null);
            }}
        />
    }

    //======================UI Functions start=====================
    function createLabelField(label, required, color = theme.text40, className) {
        return <Label className={`text-input-label m-0 p-0 px-2 pb-1 ${className}`}
                      color={color} textAt={'start'} required={required}>
            {label}
        </Label>
    }

    //======================UI Functions end=====================

    //======================Request Functions start=====================
    function handleSubmit() {
        const fun = 'handleSubmit:';
        log(fun, 'formData:', formData);
        if (UnderMaintenance) {
            toast(UnderMaintenanceMsg, CssVariant.warning);
            return false;
        }
        if (!isOnline) {
            toast(internetCheckingMsg, CssVariant.warning);
            return false;
        }

        try {
            let allFieldsChecked = checkRequiredFields();
            // allFieldsChecked = false;//for checking
            if (allFieldsChecked) {
                updateField(Fields.submitBtn_AsLoading, true);
                updateField(Fields.submitBtn_disabled, true);

                const REQUEST_OPTIONS = {
                    updateOnly: isUpdateMode(),
                    recordId: getField(Fields.updateRecordId),
                    formData: formData,
                };
                sendRequest({
                    reqUrl: ReqUrls.meeting__addEditPage_submit_formData,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);

                        const message = getJsonValue(fetchedData, 'message');
                        const success = isJsonValueTrue(fetchedData, 'success');

                        // updateField(Fields.submitBtn_disabled, false);//for testing only
                        if (success) {
                            toast(message, CssVariant.success);
                            if (showAs == ShowViewType.PAGE) {
                                redirectPage();
                            } else {
                                createTimeout(() => {
                                    clearPanelData();
                                }, 300);
                            }
                            if (onSuccess) onSuccess();
                        } else {
                            toast(message, CssVariant.danger);
                            updateField(Fields.submitBtn_disabled, false);
                        }
                        updateField(Fields.submitBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        toast(Messages.saveError, CssVariant.danger);
                        updateField(Fields.submitBtn_AsLoading, false);
                        updateField(Fields.submitBtn_disabled, false);
                    }),
                });
            }
        } catch (error) {
            logErr(fun, error);
            toast(Messages.saveError, CssVariant.danger);
            updateField(Fields.submitBtn_AsLoading, false);
            updateField(Fields.submitBtn_disabled, false);
        }
    }

    async function fetchParticipantList(selectedIntIds = [], isAddMode, formData = {}) {
        const fun = 'fetchParticipantList:';
        try {
            if (!isFieldTrue(Fields.participantsLoaded)) {
                updateField(Fields.participantsLoaded, true);
                updateField(Fields.participantsLoading, true);
                if (isBoolTrue(isAddMode)) {
                    updateField(Fields.isDataLoading, true);
                    updateField(Fields.isDataFound, false);
                    updateField(Fields.dataLoadingMsg, "");
                }

                const REQUEST_OPTIONS = {};
                sendRequest({
                    reqUrl: ReqUrls.eventForm__fetch_participantsList,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        // log(fun, 'fetchedData:', fetchedData);

                        let members = getJsonValueFromNestedKeys(fetchedData, ['data', 'members'], []);
                        let groups = getJsonValueFromNestedKeys(fetchedData, ['data', 'groups'], []);
                        // log(fun, 'fetchedData.members:', members, 'groups:', groups);

                        //select selectedIntIds by-default
                        // if (isUpdateMode()) log(fun, 'selectedIntIds:', selectedIntIds);
                        if (checkNullArr(members) && checkNullArr(selectedIntIds)) {
                            members = members.map((item) => {
                                return {
                                    ...item,
                                    selected: checkNullArr(selectedIntIds) && selectedIntIds.includes(parseInteger(getJsonValue(item, 'id'), -1))
                                };
                            });
                            updateFormField(mf.participantNamesSelectedOnly, members.filter(filterItem => isJsonValueTrue(filterItem, 'selected')));
                        }

                        updateField(Fields.participantsLoading, false);
                        updateField(Fields.participantsLoaded, true);

                        updateFormField(mf.participantNames, members);
                        updateFormField(mf.participantGroupNames, groups);
                        if (isBoolTrue(isAddMode)) {
                            updateField(Fields.isDataLoading, false);
                            updateField(Fields.isDataFound, true);
                        } else {
                            if (checkNullArr(members) && checkNullJson(formData)) {
                                const presenter = getJsonValue(formData, mf.presenter.column);
                                const foundObj = members.find(item => getJsonValue(item, NAME_KEY) == presenter);
                                // log(fun, 'presenter:', presenter, 'foundObj:', foundObj);
                                if (checkNullJson(foundObj)) {
                                    const name = getJsonValue(foundObj, NAME_KEY);
                                    if (checkNullStr(name)) {
                                        updateField(Fields.presentValueObj, {value: name, label: name});
                                    }
                                }
                            }
                        }
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        if (isBoolTrue(isAddMode)) {
                            updateField(Fields.isDataLoading, false);
                            updateField(Fields.isDataFound, true);
                        }
                        updateField(Fields.participantsLoading, false);
                        updateField(Fields.participantsLoaded, true);
                        updateField(Fields.participantsLoadingMsg, "Couldn't load participants!");
                    }),
                });
            }
        } catch (error) {
            logErr(fun, error);
            if (isBoolTrue(isAddMode)) {
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, true);
            }

            updateField(Fields.participantsLoading, false);
            updateField(Fields.participantsLoaded, true);
            updateField(Fields.participantsLoadingMsg, "Couldn't load participants!");
        }
    }

    async function fetchEditModeData(recordId, checkBefore = true) {
        const fun = 'fetchEditModeData:';
        try {
            log(fun, 'recordId:', recordId, 'isUpdateFormDataFetched:', isFieldTrue(Fields.isUpdateFormDataFetched));
            if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isUpdateFormDataFetched))) {
                updateField(Fields.isUpdateFormDataFetched, true);
                updateField(Fields.updateRecordId, recordId);
                if (checkNullStr(recordId)) {
                    updateField(Fields.isDataLoading, true);
                    updateField(Fields.dataLoadingMsg, "");

                    //send-request
                    const REQUEST_OPTIONS = {
                        recordId: recordId,
                    };
                    sendRequest({
                        reqUrl: ReqUrls.meeting__addEditPage_fetch_editFormData,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            // log(fun, 'fetchedData:', fetchedData);

                            const fetchedFormData = getJsonValue(fetchedData, 'data', {});
                            const success = checkNullJson(fetchedFormData);

                            updateField(Fields.isDataFound, success);
                            if (success) {
                                // toast('Successfully fetched your data.', CssVariant.success);

                                //modify the date-formats of meeting & repeat dates otherwise date-inputs won't show the dates
                                const meetingDate = formatDateToIoString(getJsonValue(fetchedFormData, mf.date.column));
                                const repeatDate = formatDateToIoString(getJsonValue(fetchedFormData, mf.repeatDate.column));
                                fetchedFormData[mf.date.column] = meetingDate;
                                fetchedFormData[mf.repeatDate.column] = repeatDate;
                                // log(fun, 'fetchedData.meetingDate:', meetingDate, 'fetchedFormData-new:', fetchedFormData);
                                setFormData(fetchedFormData);

                                //Update participantDisplayType, bcuz it doesn't come from database
                                updateFormField(mf.participantDisplayType, PARTICIPANT_TYPES.membersOnly);

                                //extract participants ids to be by-default selected
                                const prtscptIdsStr = getJsonValue(fetchedFormData, mf.participantNames.column) + '';
                                // log(fun, 'fetchedData.prtscptIdsStr:', prtscptIdsStr);
                                // if (checkNullStr(prtscptIdsStr)) {
                                const selectedIds = checkNullStr(prtscptIdsStr) ? prtscptIdsStr.split(',').map(item => parseInteger(trim(item), -1)) : [];
                                // log(fun, 'fetchedData.prtscptIdsStr.selectedIds:', selectedIds);
                                fetchParticipantList(selectedIds, false, fetchedFormData);
                                // }
                            } else {
                                updateField(Fields.dataLoadingMsg, "Invalid record id!");
                            }
                            updateField(Fields.isDataLoading, false);
                        }),
                        onError: ((error) => {
                            logErr(fun, error);
                            updateField(Fields.dataLoadingMsg, Messages.fetchError);
                            updateField(Fields.isDataFound, false);
                            updateField(Fields.isDataLoading, false);
                        }),
                    });
                } else {
                    updateField(Fields.dataLoadingMsg, 'Record-id not found!');
                    updateField(Fields.isDataFound, false);
                    updateField(Fields.isDataLoading, false);
                }
            }
        } catch (error) {
            logErr(fun, error);
            updateField(Fields.dataLoadingMsg, Messages.fetchError);
            updateField(Fields.isDataFound, false);
            updateField(Fields.isDataLoading, false);
        }
    }

    function redirectPage() {
        createTimeout(() => {
            panelLog(`Redirecting you to the '${moduleName}' page now...`);
            createTimeout(() => {
                window.location.href = ModuleRouteUrls[moduleName];
            }, 2500);
        }, 500);
    }

    //======================Request Functions end=====================

    //======================Util Functions start=====================
    function checkRequiredFields() {
        const fun = 'checkRequiredFields:';
        const requiredFields = [
            mf.date,
            mf.duration,
            mf.title,
            mf.outline,
            mf.desc,
            mf.venue,
            mf.presenter,
            mf.participantNamesSelectedOnly,
        ];

        let verified = false;
        let colField = {};
        for (const field of requiredFields) {
            if (field.column === mf.duration.column) {
                const durHour = parseInteger(getStrSplitArrIndexValue(getFormField(mf.duration), 0, DUR_SEP));
                const durMin = parseInteger(getStrSplitArrIndexValue(getFormField(mf.duration), 1, DUR_SEP));
                // log(fun, "requiredFields.field.duration.durHour:", durHour);
                if (durHour > 0 || durMin > 0) {
                    verified = true;
                } else {
                    colField = field;
                    verified = false;
                    break;
                }
            } else if (field.column === mf.participantNamesSelectedOnly.column) {
                // const names = (getJsonValue(formData, field.column));
                // log(fun, "requiredFields.field.participantNames.names:", names);
                if (checkNullArr(getJsonValue(formData, field.column))) {
                    verified = true;
                } else {
                    colField = field;
                    verified = false;
                    break;
                }
            } else if (!checkNullStr(formData[field.column])) {
                colField = field;
                verified = false;
                break;
            }
        }
        if (!verified) {
            // log(fun, "!verified.colField:", colField, 'idToFocus:', colField.column);
            updateFormFieldMsg(colField, 'Required field');
            toast(`'${colField.title}' cannot be empty!`, CssVariant.danger);
            focusIdField(colField.column);
        }
        // log('getParticipantList',getParticipantList());
        return verified;
    }

    function focusIdField(id, onlyFocus = false) {
        focusFormIdElement(id, onlyFocus, FOCUS_DELAY);
    }

    function getParticipantList(filtered = true) {
        if (checkNullArr(getFormField(mf.participantNames))) {
            if (getFormField(mf.participantDisplayType) == PARTICIPANT_TYPES.groupsOnly && filtered) {
                const currGroupId = getField(Fields.currParticipantGroupId);
                if (checkNullStr(currGroupId) && checkNullArr(getParticipantGroupsList())) {
                    return getJsonValue(getArrIndexValue(getParticipantGroupsList().filter(item => item.id == currGroupId), 0, {}), 'members', []);
                } else {
                    return [];
                }
            } else {
                return getFormField(mf.participantNames);
            }
        } else {
            return []
        }
    }

    function getParticipantGroupsList() {
        if (checkNullArr(getFormField(mf.participantGroupNames))) {
            return getFormField(mf.participantGroupNames);
        } else {
            return []
        }
    }

    function clearPanelData() {
        setFormFieldsMsgs({});
        setFormData({});
        setFieldsData({});
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function updateFormField(clmField, value, direct = false) {
        setFormData(prevState => ({...prevState, [direct ? clmField : clmField.column]: value}));
    }

    function getFormField(clmField, defValue, direct = false) {
        return getJsonValue(formData, direct ? clmField : clmField.column, defValue);
    }

    function isFormFieldTrue(clmField, direct = false) {
        return isBoolTrue(getFormField(clmField, null, direct), true, true);
    }

    function updateFormFieldMsg(clmField, value, direct = false) {
        setFormFieldsMsgs(prevState => ({...prevState, [direct ? clmField : clmField.column]: value}));
    }

    function getFormFieldMsg(clmField, defValue, direct = false) {
        return getJsonValue(formFieldsMsgs, direct ? clmField : clmField.column, defValue);
    }

    function isUpdateMode() {
        return isBoolTrue(updateOnly);
    }

    function getTabTitle() {
        return isUpdateMode() ? `Edit Meeting` : `Create meeting (Face to face)`;
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function panelLog(text) {
        updateField(Fields.panelMsg, text);
        updateField(Fields.panelMsgColor, theme.text90);
        log(...[text]);
    }

    function panelLogErr(...text) {
        updateField(Fields.panelMsg, text);
        updateField(Fields.panelMsgColor, 'red');
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});
export default Meeting_AddEdit_Page;
